/**
 * Pantalla de listado de órdenes por estado (reutilizable).
 *
 * La comparten las cuatro vistas de estado (pendiente / en proceso / entregada /
 * anulada) y la de búsqueda, parametrizada por `status` y `title`. Orquesta:
 *  - el filtro de estado + búsqueda + paginación como estado local de UI;
 *  - el hook de datos `useOrders` (TanStack Query) con esos filtros;
 *  - la tabla común (`OrderTable`), la paginación (`OrderPagination`) y el modal
 *    de detalle (`OrderDetailDialog`).
 *
 * Presentacional + orquestación de hooks: no hay `fetch` aquí; la sede activa
 * viaja por `X-Sede-Id` (lib/api). Estados de carga/error siempre contemplados
 * (sin pantallas en blanco, `docs/frontend_conventions.md`).
 */
import { useState } from "react";

import { Button, Card, CardContent, Input } from "@/components/ui";

import { useOrders } from "../hooks/useOrders";
import type { OrderStatus } from "../types";
import { OrderDetailDialog } from "./OrderDetailDialog";
import { OrderPagination } from "./OrderPagination";
import { OrderTable } from "./OrderTable";

/** Items por página (dentro del rango 1–100 que acota el backend). */
const PER_PAGE = 20;

interface OrdersListScreenProps {
  status: OrderStatus;
  title: string;
  /** Muestra el buscador (número o cliente). Las vistas por estado también pueden buscar. */
  searchable?: boolean;
}

/** Mensaje de error legible (usa el del `ApiError`/`Error`, con respaldo). */
function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "No se pudieron cargar las órdenes.";
}

export function OrdersListScreen({
  status,
  title,
  searchable = true,
}: OrdersListScreenProps) {
  const [page, setPage] = useState(1);
  // `search` es el término aplicado a la query; `searchInput` es lo que se teclea
  // (se aplica al enviar el formulario, no en cada pulsación).
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [detailId, setDetailId] = useState<number | null>(null);

  const orders = useOrders({ status, search, page, per_page: PER_PAGE });

  function applySearch(event: React.FormEvent) {
    event.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  }

  function clearSearch() {
    setSearchInput("");
    setSearch("");
    setPage(1);
  }

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>

      {searchable && (
        <form className="flex flex-wrap items-center gap-2" onSubmit={applySearch}>
          <label htmlFor="orders-search" className="sr-only">
            Buscar por número o cliente
          </label>
          <Input
            id="orders-search"
            className="max-w-xs"
            placeholder="Buscar por número o cliente"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
          <Button type="submit" variant="primary" size="md">
            Buscar
          </Button>
          {search && (
            <Button type="button" variant="ghost" size="md" onClick={clearSearch}>
              Limpiar
            </Button>
          )}
        </form>
      )}

      <Card>
        <CardContent className="p-0">
          {orders.isPending ? (
            <p className="px-1 py-8 text-center text-sm text-slate-500" role="status">
              Cargando órdenes…
            </p>
          ) : orders.isError ? (
            <p className="px-1 py-8 text-center text-sm text-red-600" role="alert">
              {errorMessage(orders.error)}
            </p>
          ) : (
            <>
              <OrderTable items={orders.data.items} onViewDetail={setDetailId} />
              <div className="border-t border-slate-200">
                <OrderPagination
                  page={orders.data.page}
                  pages={orders.data.pages}
                  total={orders.data.total}
                  onPageChange={setPage}
                  disabled={orders.isFetching}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <OrderDetailDialog orderId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}
